/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { AuthenticationResult, InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import * as Sentry from '@sentry/browser';

import { instantiateCombinedClient } from '../../features/azure-b2c/NativeNavigationClient';
import { useAppDispatch } from '../../store/rootStore';
import useMsalFunctions from '../../util/useMsalAuthFunctions';
import AuthSlice, {
  getAccountDetails,
  getAccounts,
  getUserDetails,
  selectCurrentAccountClaims,
  selectCurrentAccountNumber,
  selectCurrentFarmerAccount,
  selectIsCEUser,
  selectIsLoadingAuth,
} from '../../features/azure-b2c/storage';
import apiClient from '../../services/api-client';
import { useNavigate } from 'react-router-dom';
import { useScrollToTop } from '../../util/useScrollToTop';
import { useSelector } from 'react-redux';
import { getStores } from '../../features/stores/storage/stores.slice';
import AppProviders from '../../AppProviders';
import localStorage from 'redux-persist/es/storage';
import NoAccess from '../../pages/NoAccess';
import UnauthenticatedRoutes from './UnauthenticatedRoutes';
import AuthenticatedRoutes from './AuthenticatedRoutes';
import CELinkNoAccess from '../../pages/CELinkNoAccess';
import { Loader } from '../Loader';
import { isPlatformMobile } from '../../util/platform';

const AppRoutes = () => {
  const {
    instance,
    inProgress,
    accounts,
    resetMsalInstance,
    handleMsalLogout,
    createTokenRequest,
    createCELinkTokenRequest,
  } = useMsalFunctions();

  const navigate = useNavigate();
  const navigationClient = useCallback(() => instantiateCombinedClient(navigate), [navigate]);
  const dispatch = useAppDispatch();
  const [initialized, setInitialized] = useState(false);
  const [tokenFetched, setTokenFetched] = useState(false);
  const farmerAccount = useSelector(selectCurrentFarmerAccount);
  const currentAccountClaims = useSelector(selectCurrentAccountClaims);
  const isCEUser = useSelector(selectIsCEUser);
  const isLoadingAuth = useSelector(selectIsLoadingAuth);
  const ref = useRef<HTMLElement>(null);
  useScrollToTop({ ref });

  const acquiringTokenCallback = useCallback(async () => {
    if (accounts.length > 0 && inProgress === InteractionStatus.None) {
      const accountNumber = currentAccountClaims?.account_number;
      const request = accountNumber
            ? createCELinkTokenRequest('login', accountNumber)
            : createTokenRequest('login')
      try {
        const accessTokenResponse = await instance.acquireTokenSilent(request);
          setTokenFetched(true);
          return accessTokenResponse;
      } catch (error: any) {
        console.error('Error acquiring token silently:', error);
        if (error instanceof InteractionRequiredAuthError) {
          try {
            await instance.acquireTokenRedirect(request);
            return null;
          } catch (error: any) {
            await handleMsalLogout();
          }
        } else if (error.errorCode === 'user_cancelled') {
          resetMsalInstance();
        } else if (error.errorCode === 'monitor_window_timeout') {
          await handleMsalLogout();
        }
      }
    }
    return null;
  }, [inProgress, accounts, instance, currentAccountClaims]);

  const isTokenFetchedAndInitialized = tokenFetched && initialized;

  useEffect(() => {
    (async () => {
      if (accounts.length > 0 ) {
        const token = await acquiringTokenCallback();
        if (token) {
          handleToken(token);
        }
      }
    })();
  }, [accounts]);

  const handleToken = useCallback((result: AuthenticationResult) => {
    if (result.account) { 
      const account = result.account;
      if (account.idToken && !localStorage.getItem('loginTime')) {
        localStorage.setItem('loginTime', Date.now().toString());
      }
      let email = account.username;
      if (account.idTokenClaims?.emails) {
        // Not actually an array, just poor typing from microsoft
        email = account.idTokenClaims.emails as unknown as string;
      }

      Sentry.setUser({
        email: email,
        username: email,
      });

      apiClient.setDefaultToken(result.idToken);
      dispatch(AuthSlice.actions.setAuthAccount(result.account));
      setInitialized(true);
      setTokenFetched(true);
    }
  }, [dispatch]);

  useEffect(() => {
    apiClient.setGetAccessToken(acquiringTokenCallback);
    instance.setNavigationClient(navigationClient());
  }, []);

  useEffect(() => {
    if (!isPlatformMobile()) {
      const handleRedirectResponse = async () => {
        const redirectResponse = await instance.handleRedirectPromise();
        if (redirectResponse !== null) {
          handleToken(redirectResponse);
        }
      };
      handleRedirectResponse();
    }
  }, [instance, dispatch]);

  useEffect(() => {
    if (
      // Accounts are msal accounts, not farmlands 'accounts'
      accounts.length > 0 &&
      isTokenFetchedAndInitialized
    ) {
      dispatch(getAccounts());
      dispatch(getUserDetails());
      dispatch(getStores());
    }
  }, [isTokenFetchedAndInitialized, accounts]);

  const selectedAccountNumber = useSelector(selectCurrentAccountNumber);
  useEffect(() => {
    if (
      // Accounts are msal accounts, not farmlands 'accounts'
      accounts.length > 0 &&
      isTokenFetchedAndInitialized &&
      selectedAccountNumber
    ) {
      dispatch(getAccountDetails());
    }
  }, [isTokenFetchedAndInitialized, accounts, selectedAccountNumber]);

  return (
    <>
      <AuthenticatedTemplate>
        <AppProviders
          ready={isTokenFetchedAndInitialized}
          fallback={<Loader title='Loading your account' />}
        >
          {isLoadingAuth ? (
            <Loader title='Loading your account' />
          ) : farmerAccount &&
            (farmerAccount.hasShareholderAccess || farmerAccount.hasTfoAccess) ? (
            <AuthenticatedRoutes />
          ) : isCEUser ? (
            <CELinkNoAccess />
          ) : (
            <NoAccess />
          )}
        </AppProviders>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <UnauthenticatedRoutes />
      </UnauthenticatedTemplate>
    </>
  );
};

export default AppRoutes;
